import { useEffect, useRef, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import { attachProtectedVideo } from '@pixlide/protected-video';
import { cn } from '@pixlide/ui';
import { api } from '@/api/client';

interface ProtectedVideoPreviewProps {
  readonly projectId: string;

  /**
   * Identité stable de la vidéo publiée (projet + date de rendu), clé des caches du lecteur.
   * L'URL ne convient pas : elle est présignée à nouveau à chaque rafraîchissement de la liste.
   */
  readonly videoId: string;

  /** URL présignée du fichier `.pxv`, inexploitable sans la clé de lecture. */
  readonly url: string;

  /**
   * Vignette en clair (image fixe basse résolution) produite au rendu. Absente pour une vidéo
   * pas encore convertie au format protégé : le bouton de lecture s'affiche seul.
   */
  readonly posterUrl?: string | null | undefined;

  readonly name: string;
}

async function loadProjectPlaybackKey(projectId: string): Promise<string> {
  const { data } = await api.GET('/api/v1/projects/{projectId}/playback-key', {
    params: { path: { projectId } },
  });

  return data!.key;
}

/**
 * Aperçu d'une vidéo publiée, au format protégé Pixlide : déchiffrée dans le navigateur et
 * jamais proposée au téléchargement.
 *
 * Chargée au clic seulement : chaque aperçu télécharge la vidéo ENTIÈRE pour la déchiffrer,
 * ce qu'on ne peut pas imposer à toutes les cartes d'une page à son affichage. D'ici là, la
 * carte montre la vignette produite au rendu.
 */
export function ProtectedVideoPreview(props: ProtectedVideoPreviewProps) {
  const [started, setStarted] = useState(false);

  // Vignette introuvable (rendu dont la vignette a échoué) : la carte retombe sur le seul
  // bouton plutôt qu'une image cassée.
  const [posterFailed, setPosterFailed] = useState(false);
  const posterUrl = posterFailed ? null : props.posterUrl;

  if (!started) {
    return (
      <button
        type="button"
        onClick={() => setStarted(true)}
        aria-label={`Lire la vidéo ${props.name}`}
        className="group relative flex size-full items-center justify-center text-white/70 transition-colors hover:text-white"
      >
        {posterUrl && (
          <img
            src={posterUrl}
            alt=""
            loading="lazy"
            draggable={false}
            onError={() => setPosterFailed(true)}
            className="absolute inset-0 size-full object-cover"
          />
        )}
        <PlayCircle
          aria-hidden
          className={cn('relative size-10', posterUrl && 'drop-shadow-md')}
          strokeWidth={1}
        />
      </button>
    );
  }

  return <ProtectedVideoPlayer {...props} posterUrl={posterUrl} />;
}

function ProtectedVideoPlayer({ projectId, videoId, url, posterUrl, name }: ProtectedVideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);

  // L'URL change à chaque rafraîchissement de la liste (sondage pendant un rendu) : elle est
  // lue au chargement sans en faire une dépendance, pour ne pas relancer la vidéo regardée.
  const urlRef = useRef(url);

  useEffect(() => {
    urlRef.current = url;
  });

  useEffect(() => {
    const video = videoRef.current;

    if (video === null) {
      return;
    }

    const controller = new AbortController();

    attachProtectedVideo(
      video,
      { id: videoId, url: urlRef.current, loadKey: () => loadProjectPlaybackKey(projectId) },
      controller.signal,
    ).catch(() => {
      if (!controller.signal.aborted) {
        setFailed(true);
      }
    });

    return () => controller.abort();
  }, [projectId, videoId]);

  if (failed) {
    return (
      <span role="alert" className="px-3 text-center text-xs text-white/70">
        Lecture impossible. Rechargez la page pour réessayer.
      </span>
    );
  }

  return (
    <video
      ref={videoRef}
      poster={posterUrl ?? undefined}
      controls
      autoPlay
      playsInline
      aria-label={`Vidéo ${name}`}
      className="size-full object-cover"
      disablePictureInPicture
      controlsList="nodownload noremoteplayback"
      onContextMenu={(event) => event.preventDefault()}
    />
  );
}
